<template>

  <div>

    <div :id="_uid" class="ui modal" :class="modalSelector">
      <div class="ui header">
        {{ isCreating ? "Nova Ficha Cadastral" : "Alterar Ficha Cadastral" }}
      </div>
      <div class="content scrolling">
        <div class="ui form" :class="{ loading }">
          <div class="fields">
            <div class="eight wide field">
              <label class="form-label">Clínica <span class="red text">*</span></label>
              <customer-picker ref="customerPicker" :authToken="authToken" @onClear="onClearCustomer" @onPick="onPickCustomer" />
            </div>               
            <div class="eight wide field">
              <label class="form-label">Animal <span class="red text">*</span></label>
              <animal-picker ref="animalPicker" :authToken="authToken" @onClear="onClearAnimal" @onPick="onPickAnimal" />
            </div>
          </div>
          <div class="fields">
            <div class="six wide field">
              <label class="form-label">Raça</label>
              <input :value="registration.animal ? registration.animal.race.name : ''" readonly="readonly">
            </div>
            <div class="six wide field">
              <label class="form-label">Espécie</label>
              <input :value="registration.animal ? registration.animal.race.species.name : ''" readonly="readonly">
            </div>     
            <div class="four wide field">
              <label class="form-label">Sexo <i :class="animalSex.icon"></i></label>
              <input :value="animalSex.label" readonly="readonly">
            </div>                                   
          </div>
          <div class="fields">
            <div class="eight wide field">
              <label class="form-label">Tutor</label>
              <input :value="registration.owner ? registration.owner.name : ''" readonly="readonly">
            </div>
            <div class="four wide field">
              <label class="form-label">Número da Ficha</label>
              <input v-model="registration.registrationId">
            </div>    
          </div>
        </div>
      </div>
      <div class="actions">
        <button class="ui cancel basic button">
          <i class="times icon"></i>
          Cancelar
        </button>      
        <button v-if="isUpdating" class="ui basic red button" 
          :class="{ loading: checkingRemoval || removing, disabled: saving }" @click="onRemove()">
          <i class="trash icon"></i> Excluir
        </button>
        <button class="ui blue button" :class="{ loading: saving, disabled: !registration.customerId || !registration.animalId || removing }" @click="onConfirm()">
          <i class="checkmark icon"></i>
          {{ isCreating ? "Confirmar" : "Atualizar" }}
        </button>
      </div>
    </div>

    <can-not-remove-modal ref="canNotRemoveModal" />

    <yes-no-modal ref="confirmRemovalModal" :message="removalMessage" :danger="true" @onYes="onConfirmRemoval()"/>

  </div>
  
</template>

<script>

  import modalDialog from "@/mixins/modal-dialog"
  import AnimalRegistrationService from "@/services/animal-registration-service"
  import { AnimalPicker, CustomerPicker } from "@/components"

  export default {

    name: "AnimalRegistrationFormModal",

    mixins: [modalDialog],

    data() {
      return {
        loading: true,
        authToken: null,
        registration: {},
        service: new AnimalRegistrationService(),
      }
    },

    async created() {   
      this.authToken = await this.$store.state.auth.fbUser.getIdToken(true)      
      this.loading = false
    },

    components: {
      AnimalPicker,
      CustomerPicker,
    },

    computed: {

      animalSex() {

        let sex = { icon: "", label: "" }  

        if (!this.registration.animal)
          return sex

        if (this.registration.animal.sex == "male") {
          sex = { icon: "mars blue icon", label: "Macho" }
        } else if (this.registration.animal.sex == "female") {
          sex = { icon: "venus pink icon", label: "Fêmea" }
        } else if (this.registration.animal.sex == "undefined") {
          sex = { icon: "question circle grey icon", label: "Indefinido" }
        }

        return sex
      },

      removalMessage() {

        if (!this.registration.animal || !this.registration.customer)
          return ""

        return `Confirma exclusão da ficha cadastral do animal ${this.registration.animal.name} na clínica ${this.registration.customer.shortName} ?`  
      },
    },
    
    methods: {

      load(registration) {

        this.registration = {}

        this.$refs.customerPicker.clear()
        this.$refs.animalPicker.clear()

        if (registration)
          this.registration = this.$cloneObject(registration)

        if (registration && this.registration.customer) {  
          this.registration.customerId = this.registration.customer.id
          this.$refs.customerPicker.setSelected(this.registration.customer)
        }
        
        if (registration && this.registration.animal) {
          this.registration.animalId = this.registration.animal.id
          this.registration.ownerId = this.registration.animal.ownerId
          this.$refs.animalPicker.setSelected(this.registration.animal)
        }  

        this.show()
      },

      onPickCustomer(customer) {
        this.registration.customerId = customer.id
        this.registration.customer = customer
        this.refreshData()
      },

      onClearCustomer() {
        this.registration.customerId = null
        this.refreshData()
      },

      onPickAnimal(animal) {
        this.registration.animalId = animal.id
        this.registration.animal = animal
        this.registration.ownerId = animal.ownerId
        this.registration.owner = animal.owner
        this.refreshData()
      },

      onClearAnimal() {
        this.registration.animalId = null
        this.registration.ownerId = null
        this.refreshData()
      },

      refreshData() {
        this.registration = this.$cloneObject(this.registration)
      },

      async onConfirm() {

        if (this.saving)
          return

        this.saving = true
        
        try {

          if (this.isCreating) {

            const newRegistration = await this.service.create(this.registration)

            this.registration.id = newRegistration.id

          } else {            

            await this.service.update(this.registration)            
          }

          this.emit("onSave", this.registration)

          this.$msg.success(this.isCreating ? "Ficha criada com sucesso" : "Ficha alterada com sucesso")
          
        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.saving = false
        }
      },

      async onRemove() {

        this.checkingRemoval = true

        try {

          const data = await this.service.canRemove(this.registration)

          if (data.canRemove)  {
            this.$refs.confirmRemovalModal.show()              
          } else {
            this.$refs.canNotRemoveModal.show(data.entities)            
          }

        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.checkingRemoval = false
        }        
      },

      async onConfirmRemoval() {

        if (this.removing)
          return

        this.removing = true

        try {

          await this.service.remove(this.registration) 

          this.emit("onRemove", this.registration)

          this.$msg.success("Ficha excluída com sucesso")
          
        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.removing = false
        }
      },
    },
  }

</script>
